import * as Sentry from '@sentry/react';
import { useQueryClient } from '@tanstack/react-query';
import { useUploadBenchmarkImageMutation } from '@vizit/api';
import { SENTRY_FEATURE_TAGS, SENTRY_STAGE_TAGS } from '@vizit/constants';
import type { UploadQueueFileInput } from '@vizit/organisms';
import { type S3UploadFields, uploadToS3WithProgress } from '@vizit/s3';
import * as React from 'react';
import { EMPTY_BENCHMARK_UPLOAD_STATE, getBenchmarkUploadStore } from './benchmark-upload-store';

const subscribeToNothing = () => () => undefined;
const getEmptyState = () => EMPTY_BENCHMARK_UPLOAD_STATE;

interface UploadBatchOptions {
  /**
   * Called once per batch, when the first of its files reaches storage: that is when the API starts acting on the
   * upload. Files that fail, or that are removed from the queue before they start, change nothing about it, so
   * nothing waits on them.
   */
  onFirstLanded?: () => void;
}

interface UseBenchmarkImageUploadProps {
  workspaceId?: string;
  benchmarkId: string;
  /**
   * Sent with each upload when set. The API asks for one, but a benchmark has none until a category is added to it.
   */
  productCategoryId?: string;
}

/**
 * Turns chosen files into upload queue items for a benchmark. Each item creates the image record, asks for a refresh
 * of the benchmark so its tile shows up, sends the file to S3 with the presigned POST the API returned, then asks for
 * another and reports the landing as `uploadLandedAt`: the API scores the new file under each of the benchmark's
 * categories a moment later, which `useBenchmarkScorings` watches for. The refreshes of every item of a benchmark,
 * whatever its batch, share one throttle (see `UPLOAD_REFRESH_INTERVAL_MS` in `benchmark-upload-store`), and no item
 * waits on them. `uploadingImageIds` and `failedImageIds` say which tiles are still uploading or never got their file.
 * All of it is kept per benchmark outside the page, as the uploads outlive it: a page opened again mid-upload shows
 * the running batch and shares its throttle. A failure is reported to Sentry and rethrown so the upload queue marks
 * the item failed.
 */
function useBenchmarkImageUpload({ workspaceId, benchmarkId, productCategoryId }: UseBenchmarkImageUploadProps) {
  const queryClient = useQueryClient();
  const { mutateAsync } = useUploadBenchmarkImageMutation();

  // The API cannot say when a file has landed, so the page learns it from the uploads it runs itself. They outlive
  // the page, so their state lives in a store per benchmark that a page opened again picks up.
  const store = workspaceId ? getBenchmarkUploadStore(queryClient, workspaceId, benchmarkId) : undefined;
  const { uploadingImageIds, failedImageIds, uploadLandedAt, isRefreshing } = React.useSyncExternalStore(
    store?.subscribe ?? subscribeToNothing,
    store?.getSnapshot ?? getEmptyState,
  );

  const uploadFile = React.useCallback(
    async (file: File, onProgress: (progress: number) => void) => {
      if (!workspaceId) {
        throw new Error('Cannot upload to a benchmark before the workspace is known.');
      }

      const extra = {
        file_name: file.name,
        file_size: file.size,
        workspace_id: workspaceId,
        benchmark_id: benchmarkId,
      };
      const tags = {
        feature: SENTRY_FEATURE_TAGS.Benchmark,
        stage: SENTRY_STAGE_TAGS[SENTRY_FEATURE_TAGS.Benchmark].benchmarkImageUpload,
      };

      const uploads = getBenchmarkUploadStore(queryClient, workspaceId, benchmarkId);
      try {
        const upload = await mutateAsync({
          workspace_id: workspaceId,
          benchmark_id: benchmarkId,
          name: file.name,
          product_category_id: productCategoryId,
        });

        // The record exists now, so its tile shows as processing while the file is on its way to S3.
        uploads.markUpload(upload.imageId, 'uploading');
        uploads.requestRefresh();

        try {
          await uploadToS3WithProgress(
            upload.imageId,
            upload.uploadUrl,
            file,
            upload.uploadFields as unknown as S3UploadFields,
            onProgress,
            async () => uploads.markUpload(upload.imageId, 'done'),
            () => undefined,
          );
        } catch (error) {
          uploads.markUpload(upload.imageId, 'failed');
          throw error;
        }

        // The file is in storage: the tile can load it, and the API will score it under the benchmark's categories.
        // Only the API's clock can date that; a record without one leaves the previous landing in place.
        uploads.markLanded(upload.createdAt);
        uploads.requestRefresh();
      } catch (error) {
        Sentry.captureException(error, { level: 'error', tags, extra });
        throw error;
      }
    },
    [queryClient, workspaceId, benchmarkId, productCategoryId, mutateAsync],
  );

  const toUploadQueueItems = React.useCallback(
    (files: File[], { onFirstLanded }: UploadBatchOptions = {}): UploadQueueFileInput[] => {
      let hasLanded = false;

      return files.map((file) => ({
        file,
        onUpload: async (onProgress) => {
          await uploadFile(file, onProgress);
          if (!hasLanded) {
            hasLanded = true;
            onFirstLanded?.();
          }
        },
      }));
    },
    [uploadFile],
  );

  return {
    toUploadQueueItems,
    uploadingImageIds,
    failedImageIds,
    /** When the record of the latest upload to reach storage was created, by the API's clock; `null` until one has */
    uploadLandedAt,
    /** Whether the benchmark's lists are still to be read since an upload changed it; until then they may not show it */
    isRefreshing,
  };
}

export { useBenchmarkImageUpload };
